import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import axios from "../axios-config";

type DemoAccount = {
    role?: string;
    username?: string;
    email?: string;
    password?: string;
};

type DemoResponse = {
    email?: string;
    demoHost?: string;
    expiresOn?: string;
    accounts?: DemoAccount[];
    emailSent?: boolean;
    message?: string;
    errors?: string[];
};

const SignUp: React.FC = () => {
    const [email, setEmail] = useState("");
    const [fullName, setFullName] = useState("");
    const [schoolName, setSchoolName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [messageHtml, setMessageHtml] = useState<string | null>(null);
    const [isError, setIsError] = useState(false);

    const resetMessages = () => {
        setMessageHtml(null);
        setIsError(false);
    };

    const showMessage = (text: string, error = false) => {
        setMessageHtml(`<p>${text}</p>`);
        setIsError(error);
    };

    const showSuccess = (data: DemoResponse) => {
        // Simple HTML and attribute escaping to safely inject dynamic content
        const escapeHtml = (s: string) =>
            s
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/\"/g, "&quot;")
                .replace(/'/g, "&#39;");

        const escapeAttr = (s: string) =>
            s
                .replace(/&/g, "&amp;")
                .replace(/"/g, "&quot;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/'/g, "&#39;");

        const expires = data?.expiresOn
            ? new Date(data.expiresOn).toLocaleString()
            : "in 30 days";

        let html = `
            <p>Your credentials were emailed to <strong>${data?.email ?? "you"}</strong>.
            Access the environment at <a href=\"https://${data?.demoHost ?? "demo.acornschools.com"}\" target=\"_blank\" rel=\"noreferrer\">${data?.demoHost ?? "demo.acornschools.com"}</a>.</p>
            <p>These accounts expire on <strong>${expires}</strong>.</p>`;

        if (Array.isArray(data?.accounts) && data.accounts.length) {
            const rows = data.accounts
                .map((acct) => {
                    const role = escapeHtml(acct.role ?? "");
                    const username = acct.username ?? "";
                    const password = acct.password ?? "";
                    const usernameHtml = escapeHtml(username);
                    const passwordHtml = escapeHtml(password);
                    const usernameAttr = escapeAttr(username);
                    const passwordAttr = escapeAttr(password);

                    return `
                        <tr>
                            <td>${role}</td>
                            <td>
                                <span class="mono">${usernameHtml}</span>
                                <button type="button" class="copy-btn" aria-label="Copy username" data-copy="${usernameAttr}">Copy</button>
                            </td>
                            <td>
                                <span class="mono">${passwordHtml}</span>
                                <button type="button" class="copy-btn" aria-label="Copy password" data-copy="${passwordAttr}">Copy</button>
                            </td>
                        </tr>`;
                })
                .join("");

            const host = data?.demoHost ?? "demo.acornschools.com";
            const loginHref = `https://${host}/login`;
            const loginHrefEsc = escapeAttr(loginHref);
            const hostHtml = escapeHtml(host);

            html += `
                <div style="display:flex;flex-direction:column;align-items:center;">
                    <p style="text-align:center">Here are the demo credentials for quick reference:</p>
                    <br/>
                    <style>
                        .accounts { width:auto; border-collapse: collapse; margin: 0 auto; }
                        .accounts th, .accounts td { padding: 8px 12px; text-align: left; vertical-align: top; }
                        .accounts th { font-weight: 600; }
                        .accounts .mono { font-family: ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,\"Liberation Mono\",\"Courier New\",monospace; }
                        .accounts .copy-btn { margin-left: 8px; padding: 2px 8px; font-size: 0.875rem; border: 1px solid #e5e7eb; border-radius: 6px; background: #f9fafb; color: #374151; cursor: pointer; }
                        .accounts .copy-btn:hover { background: #f3f4f6; }
                        .login-cta { margin-top: 16px; text-align: center; }
                        .login-cta a.login-link { display: inline-block; text-decoration: none; background: linear-gradient(135deg,#6366f1,#8b5cf6); color: #fff; padding: 10px 14px; border-radius: 999px; font-weight: 600; }
                        .login-cta a.login-link:hover { filter: brightness(0.95); }
                    </style>
                    <table class="accounts"> 
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Username</th>
                                <th>Password</th>
                            </tr>
                        </thead>
                        <tbody>${rows}</tbody>
                    </table>
                    <div class="login-cta">
                        <a class="login-link" href="${loginHrefEsc}" target="_blank" rel="noreferrer">Open the login page for ${hostHtml}</a>
                    </div>
                </div>`;
        }

        if (data?.emailSent === false) {
            html += `<p style="margin-top:16px;color:#b45309;">We couldn\'t send the confirmation email, but the accounts are ready. Please copy the credentials above.</p>`;
        }

        setMessageHtml(html);
        setIsError(false);
    };

    // Wire up copy buttons inside the success message using event delegation
    useEffect(() => {
        if (!messageHtml) return;
        const container = document.getElementById("message");
        if (!container) return;

        const handler = async (ev: MouseEvent) => {
            const target = ev.target as HTMLElement | null;
            const btn = target?.closest?.('button.copy-btn') as HTMLButtonElement | null;
            if (!btn) return;
            ev.preventDefault();

            const text = btn.getAttribute('data-copy') ?? '';
            if (!text) {
                try { toast.error('Nothing to copy'); } catch {}
                return;
            }

            let ok = false;
            try {
                if (navigator.clipboard && (window as any).isSecureContext) {
                    await navigator.clipboard.writeText(text);
                    ok = true;
                } else {
                    // Fallback for older browsers or insecure context
                    const ta = document.createElement('textarea');
                    ta.value = text;
                    ta.style.position = 'fixed';
                    ta.style.left = '-9999px';
                    document.body.appendChild(ta);
                    ta.focus();
                    ta.select();
                    ok = document.execCommand('copy');
                    document.body.removeChild(ta);
                }
            } catch {
                ok = false;
            }

            const original = btn.textContent || 'Copy';
            if (ok) {
                btn.textContent = 'Copied';
                setTimeout(() => { btn.textContent = original; }, 1200);
                try { toast.success('Copied to clipboard'); } catch {}
            } else {
                try { toast.error('Copy failed'); } catch {}
            }
        };

        container.addEventListener('click', handler);
        return () => {
            container.removeEventListener('click', handler);
        };
    }, [messageHtml]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        resetMessages();

        const trimmedEmail = email.trim();
        const payload = {
            email: trimmedEmail,
            fullName: fullName.trim() || null,
            schoolName: schoolName.trim() || null,
        };

        if (!payload.email) {
            showMessage("Please enter a valid email address.", true);
            return;
        }

        setSubmitting(true);
        showMessage("Submitting your request…");

        try {
            const resp = await axios.post<DemoResponse>("/api/Account/demo-signup", payload, {
                headers: { "Content-Type": "application/json" },
            });

            if (resp.status >= 200 && resp.status < 300) {
                showSuccess(resp.data ?? {});
                setEmail("");
                setFullName("");
                setSchoolName("");
            } else {
                const data = resp.data as DemoResponse;
                if (resp.status === 409) {
                    showMessage(data?.message ?? "An account already exists for this email address.", true);
                } else if (resp.status === 400 && Array.isArray(data?.errors)) {
                    showMessage((data.errors || []).join(" "), true);
                } else {
                    showMessage(data?.message ?? "Something went wrong while provisioning demo accounts.", true);
                }
            }
        } catch (err: any) {
            const resp = err?.response;
            if (resp) {
                const data = resp.data as DemoResponse;
                if (resp.status === 409) {
                    showMessage(data?.message ?? "An account already exists for this email address.", true);
                } else if (resp.status === 400 && Array.isArray(data?.errors)) {
                    showMessage((data.errors || []).join(" "), true);
                } else {
                    showMessage(data?.message ?? "Something went wrong while provisioning demo accounts.", true);
                }
            } else {
                console.error(err);
                showMessage("We couldn\u2019t reach the server. Please try again in a moment.", true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg,#eef3ff,#fef6ed)", padding: 24 }}>
            <main style={{ width: "min(880px,100%)", background: "#fff", borderRadius: 16, boxShadow: "0 24px 48px rgba(15,23,42,0.12)", padding: 32 }}>
                <h1 style={{ marginTop: 0, fontSize: "1.75rem", color: "#1f2937" }}>Try the AcornJr Demo</h1>
                <p style={{ color: "#4b5563", lineHeight: 1.5 }}>
                    Tell us where to send your credentials and we9;ll spin up a demo administrator plus pre-built super teacher, teacher, and student accounts for the next 30 days.
                </p>

                <form onSubmit={handleSubmit} noValidate style={{ display: "grid", gap: 16, marginTop: 16 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        <label htmlFor="email" style={{ flex: '1 1 calc(50% - 8px)', minWidth: 220, fontWeight: 600, color: '#374151' }}>
                            Email
                            <input
                                id="email"
                                name="email"
                                type="email"
                                placeholder="you@example.com"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                style={{ marginTop: 6, border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '1rem', width: '100%' }}
                            />
                        </label>

                        <label htmlFor="fullName" style={{ flex: '1 1 100%', minWidth: 220, fontWeight: 600, color: '#374151' }}>
                            Full Name <span style={{ fontWeight: 400, color: '#9ca3af' }}></span>
                            <input
                                id="fullName"
                                name="fullName"
                                type="text"
                                placeholder="Jane Doe"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                style={{ marginTop: 6, border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '1rem', width: '100%' }}
                            />
                        </label>

                        <label htmlFor="schoolName" style={{ flex: '1 1 100%', fontWeight: 600, color: '#374151' }}>
                            School name <span style={{ fontWeight: 400, color: '#9ca3af' }}></span>
                            <input
                                id="schoolName"
                                name="schoolName"
                                type="text"
                                placeholder="Example Elementary School"
                                value={schoolName}
                                onChange={(e) => setSchoolName(e.target.value)}
                                style={{ marginTop: 6, border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: '1rem', width: '100%' }}
                            />
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                       className={`rounded-full px-4 py-3 font-semibold text-gray-900 bg-gradient-to-br from-white to-blue-500
        ${submitting ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                        {submitting ? 'Requesting…' : 'Request demo access'}
                    </button>
                </form>

                <section id="message" role="status" aria-live="polite" style={{ marginTop: 24, padding: 16, borderRadius: 12, background: isError ? "#fef2f2" : "#f9fafb", color: isError ? "#b91c1c" : "#111827", display: messageHtml ? "block" : "none" }} dangerouslySetInnerHTML={{ __html: messageHtml ?? "" }} />
                <Toaster richColors position="top-right" />
                <div className="mb-8 flex justify-center"><br/></div>
                <div className="mb-8 flex justify-center">
                    <img
                        src="https://i.imgur.com/UE5wDds.png"
                        alt="AcornJr Logo"
                        className="w-24 h-24"
                    />
                </div>
            </main>
        </div>
    );
};

export default SignUp;
